import React, { useEffect, useMemo } from 'react';

import get from 'lodash/get';
import isArray from 'lodash/isArray';

import { SidePage, Section, IconButton } from '@gogame/components';
import { useMountedState, useChildComponent } from '@gogame/hooks-ts';

import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import {
  AffiliateFormCreate,
  AffiliateFormEdit,
} from 'scenes/Affiliates/components/AffiliateForm';
import { BusinessPartnerFormEdit } from 'scenes/BusinessPartners/components/BusinessPartnerForm';
import {
  useBusinessPartnerDetails,
  useIsUpdating,
} from 'scenes/BusinessPartners/data';
import IODetails from 'scenes/IO/components/IODetails/IODetails';
import { IOFormCreate } from 'scenes/IO/components/IOForm';
import PartnerCompanyDetails from 'scenes/PartnerCompanies/components/PartnerCompanyDetails';

import BPActions from '../Actions';
import ActionsLog from './ActionsLog';
import Affiliates from './Affiliates';
import Attachments from './Attachments';
import ContactPerson from './ContactPerson';
import Contacts from './Contacts';
import General from './General';
import IOs from './IOs';
import Metadata from './Metadata';
import { defaultBaseURL } from 'api';

export const TAB_DETAILS = 'Details';
export const TAB_ACTIONS_LOG = 'Actions Log';
export const TAB_ATTACHMENTS = 'Attachments';

const BusinessPartnerDetails = ({
  id,
  onClose,
  onExited,
  open,
  activeTabId = TAB_DETAILS,
}) => {
  const { data, isLoading, isRefetching } = useBusinessPartnerDetails({
    id,
  });
  const [childComponent, openChildComponent] = useChildComponent();
  const [currentVersion, setCurrentVersion] = useMountedState(0);

  const currentDetails = get(data, ['details', currentVersion], {});
  const isUpdating = useIsUpdating();

  useEffect(() => {
    if (isArray(data?.details)) {
      setCurrentVersion(data.details.length - 1);
    } else {
      setCurrentVersion(0);
    }
  }, [data]);

  const handleEdit = () => {
    openChildComponent(BusinessPartnerFormEdit, {
      id: currentDetails.id,
    });
  };

  const handleDelete = async () => {
    onClose();
  };

  const handleOpenPartnerCompany = (_e, { id }) => {
    openChildComponent(PartnerCompanyDetails, {
      id,
    });
  };

  const handleOpenAffiliates = async (_e, { id }) => {
    openChildComponent(AffiliateDetails, {
      id,
    });
  };

  const handleOpenIo = async (_e, { id }) => {
    openChildComponent(IODetails, {
      id,
    });
  };

  const handleAddAffiliates = async () => {
    openChildComponent(AffiliateFormCreate, {
      businessPartner: currentDetails.id,
    });
  };

  const handleAddIO = async () => {
    openChildComponent(IOFormCreate, {
      businessPartner: currentDetails.id,
      businessPartnerVersion: currentDetails.version,
      initialNormalized: false,
    });
  };

  const handleEditAffiliates = async (_e, { id }) => {
    openChildComponent(AffiliateFormEdit, {
      id,
    });
  };

  const handleChangeVersion = (v) => setCurrentVersion(v);

  const ioCount = currentDetails?.ioAttachments?.length || 0;
  const affiliateCount = currentDetails?.affiliates?.length || 0;

  const dpa = useMemo(() => {
    if (!currentDetails?.dpaAttachment || !currentDetails?.dpa) {
      return null;
    }
    return {
      label: 'DPA',
      name: currentDetails.dpaAttachment.name,
      withPreview: currentDetails.dpaAttachment.withPreview,
      src: `${defaultBaseURL}/api/business-partners/dpa/download/${currentDetails?.dpa.id}`,
    };
  }, [currentDetails?.id, currentDetails?.dpa, currentDetails?.dpaAttachment]);

  const nda = useMemo(() => {
    if (!currentDetails?.ndaAttachment || !currentDetails?.nda) {
      return null;
    }
    const { name, withPreview } = currentDetails.ndaAttachment;
    return {
      label: 'NDA',
      name,
      withPreview,
      src: `${defaultBaseURL}/api/business-partners/nda/download/${currentDetails?.nda.id}`,
    };
  }, [currentDetails?.id, currentDetails?.nda, currentDetails?.ndaAttachment]);

  const attachments = useMemo(() => {
    return [...(dpa ? [dpa] : []), ...(nda ? [nda] : [])];
  }, [nda, dpa]);

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading || isUpdating}
      fetching={isRefetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Business Partner'}
        subTitle={currentDetails.name}
        entityId={`${id}${
          currentDetails?.sapId ? ` SAP # ${currentDetails.sapId}` : ''
        }`}
      />
      <SidePage.Tabs activeTabId={activeTabId}>
        <SidePage.Tab label={TAB_DETAILS}>
          <SidePage.NewBody wrapper={true} dense={true}>
            <SidePage.NewContent>
              <Section title={'General'}>
                <General
                  data={currentDetails}
                  onOpenPartnerCompany={handleOpenPartnerCompany}
                />
              </Section>
              <Section
                title={'Contact Person'}
                isCollapsable={true}
                initialOpen={false}
              >
                <ContactPerson data={currentDetails} />
              </Section>
              <Section
                title={'Contacts'}
                isCollapsable={true}
                initialOpen={false}
              >
                <Contacts data={currentDetails} />
              </Section>
              <Section
                title={`IOs (${ioCount})`}
                isCollapsable={!!ioCount}
                initialOpen={false}
                action={
                  <IconButton add tooltip={'Add IO'} onClick={handleAddIO} />
                }
              >
                <IOs
                  data={currentDetails?.ioAttachments}
                  onOpenIo={handleOpenIo}
                />
              </Section>
              <Section
                title={`Affiliates (${affiliateCount})`}
                isCollapsable={!!affiliateCount}
                initialOpen={false}
                action={
                  <IconButton
                    add
                    tooltip={'Add Affiliate'}
                    onClick={handleAddAffiliates}
                  />
                }
              >
                <Affiliates
                  data={currentDetails?.affiliates}
                  onOpenAffiliates={handleOpenAffiliates}
                  onEditAffiliates={handleEditAffiliates}
                />
              </Section>
            </SidePage.NewContent>
            <SidePage.Sidebar
              actions={
                <BPActions
                  actions={currentDetails.actions}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  data={currentDetails}
                  isFirstVersion={currentVersion === 0}
                />
              }
            >
              <Metadata
                data={currentDetails}
                currentVersion={currentVersion}
                onChangeVersion={handleChangeVersion}
                versions={data?.details}
              />
            </SidePage.Sidebar>
          </SidePage.NewBody>
        </SidePage.Tab>
        <SidePage.Tab label={TAB_ATTACHMENTS} count={attachments.length || '0'}>
          <SidePage.NewBody>
            <Attachments data={attachments} />
          </SidePage.NewBody>
        </SidePage.Tab>
        <SidePage.Tab label={TAB_ACTIONS_LOG}>
          <SidePage.NewBody>
            <ActionsLog data={data?.actionsLog} />
          </SidePage.NewBody>
        </SidePage.Tab>
      </SidePage.Tabs>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default BusinessPartnerDetails;
